import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const sections = [
  {
    id: 1,
    title: "1. Wie zijn wij?",
    content: (
      <div className="space-y-4">
        <p>
          Verduurzaamwijzer.nu is een onafhankelijk informatie- en adviesplatform voor huiseigenaren. Het platform
          selecteert en beveelt verduurzamingsoplossingen aan op basis van eigen kennis, ervaring en professionele
          beoordeling. Aanbevelingen vormen geen uitputtende vergelijking van de gehele markt.
        </p>
        <p>De verwerkingsverantwoordelijke voor de persoonsgegevens die via het platform worden verwerkt:</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <tbody>
              {[
                ["Verwerkingsverantwoordelijke", "De Content Factory BV"],
                ["Handelsnaam", "Verduurzaamwijzer.nu"],
                ["Vestigingsadres", "Oude Middenweg 17, 2491 AC Den Haag"],
                ["KvK-nummer", "84512490"],
                ["Btw-nummer", "8632.40.100.B.01."],
                ["E-mail privacy", "contact@verduurzaamwijzer.nu"],
                ["Telefoonnummer", "06 4765 0537"],
              ]?.map(([label, value], i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-muted/40" : ""}>
                  <td className="py-2 px-4 font-medium text-foreground/80 w-1/2 border border-border/40">{label}</td>
                  <td className="py-2 px-4 text-foreground/70 border border-border/40">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  {
    id: 2,
    title: "2. Welke gegevens verwerken wij?",
    content: (
      <div className="space-y-3">
        <p>
          Afhankelijk van uw gebruik van de website kunnen wij naam, contactgegevens, postcode en adresgegevens,
          informatie over uw woning, energieverbruik, bestaande installaties, wensen, budget, berichten en
          aanvraaginhoud verwerken. Daarnaast kunnen technische gegevens zoals IP-adres, apparaat- en
          browserinformatie, loggegevens en cookievoorkeuren worden verwerkt.
        </p>
        <p className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
          Wij vragen u geen bijzondere persoonsgegevens te verstrekken, zoals medische gegevens, religie of een
          burgerservicenummer. Vermeld zulke gegevens niet in vrije tekstvelden.
        </p>
      </div>
    ),
  },
  {
    id: 3,
    title: "3. Doeleinden en rechtsgrondslagen",
    content: (
      <div className="space-y-3">
        <p>Wij verwerken gegevens om:</p>
        <ul className="space-y-2 list-none">
          {[
            ["Vragen en adviesaanvragen afhandelen", "contact met u opnemen op basis van gerechtvaardigd belang of stappen voorafgaand aan een overeenkomst, afhankelijk van de situatie."],
            ["Passende oplossingen beoordelen", "en op uw verzoek een introductie bij een aanbieder mogelijk te maken op basis van stappen voorafgaand aan een overeenkomst of toestemming, afhankelijk van de inrichting."],
            ["De website veilig en functioneel houden", "op basis van gerechtvaardigd belang."],
            ["Aan wettelijke verplichtingen voldoen", "op basis van een wettelijke verplichting."],
          ]?.map(([label, desc], i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              <span><strong>{label}:</strong> {desc}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm text-foreground/70">
          Voor optionele marketing en niet-noodzakelijke tracking vragen wij waar vereist vooraf toestemming.
          De exacte rechtsgrond sluit aan op de feitelijke verwerking.
        </p>
      </div>
    ),
  },
  {
    id: 4,
    title: "4. Doorgifte aan leveranciers en installatiepartners",
    content: (
      <div className="space-y-3">
        <p>
          Wanneer u een offerte, installatie of vervolgadvisie aanvraagt, kunnen relevante gegevens worden gedeeld
          met één of meer geselecteerde partners, maar uitsluitend voor het vooraf duidelijk gemaakte doel en op
          een toepasselijke rechtsgrond. Vóór verzending informeren wij u over de betrokken partner(s) of de
          categorie ontvangers, het doel en de gevolgen van de doorgifte.
        </p>
        <p>
          Partners kunnen zelfstandig verwerkingsverantwoordelijke zijn voor hun eigen offerte- en klantproces.
          Zij moeten dan zelf uitleggen hoe zij uw gegevens verwerken.
        </p>
        <p className="text-sm bg-primary/5 border border-primary/20 rounded-lg p-3 text-foreground/70">
          Wij verkopen uw persoonsgegevens niet als los handelsbestand.
        </p>
      </div>
    ),
  },
  {
    id: 5,
    title: "5. Commerciële samenwerkingen en communicatie",
    content: (
      <div className="space-y-3">
        <p>
          Verduurzaamwijzer.nu kan een vergoeding ontvangen voor doorverwijzingen of samenwerkingen. Dit verandert
          niet automatisch de privacyrechtelijke grondslag voor gegevensdeling.
        </p>
        <p>
          Een adviesaanvraag geldt niet zonder meer als toestemming voor nieuwsbrieven of ongevraagde commerciële
          berichten. Daarvoor passen wij de relevante toestemmings- en communicatieregels toe.
        </p>
      </div>
    ),
  },
  {
    id: 6,
    title: "6. Cookies en vergelijkbare technieken",
    content: (
      <div className="space-y-3">
        <p>
          De website kan functionele cookies gebruiken die noodzakelijk zijn voor de werking. Voor analytische en
          marketingcookies gelden aanvullende informatie- en, waar vereist, toestemmingsverplichtingen.
        </p>
        <ul className="space-y-2 list-none">
          {[
            ["Functioneel", "voor beveiliging, sessies, voorkeuren en het functioneren van formulieren."],
            ["Analytics", "om geaggregeerd inzicht te krijgen in gebruik en prestaties. Wij richten deze waar mogelijk privacyvriendelijk in."],
            ["Marketing en tracking", "om campagnes te meten, doelgroepen te vormen of inhoud en advertenties te personaliseren. Deze worden alleen geplaatst na geldige toestemming."],
          ]?.map(([label, desc], i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              <span><strong>{label}:</strong> {desc}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm text-foreground/70">
          Niet-noodzakelijke cookies worden pas geplaatst nadat geldige toestemming is verkregen, voor zover die
          vereist is. U kunt uw keuze op ieder moment wijzigen via de cookie-instellingen.
        </p>
      </div>
    ),
  },
  {
    id: 7,
    title: "7. Bewaartermijnen",
    content: (
      <div className="space-y-3">
        <p>
          Wij bewaren persoonsgegevens niet langer dan noodzakelijk voor het doel waarvoor ze zijn verzameld.
          Wettelijke bewaarplichten kunnen een langere termijn vereisen. Na afloop verwijderen of anonimiseren
          wij de gegevens.
        </p>
        <ul className="space-y-2 list-none">
          {[
            ["Contactaanvragen", "maximaal 2 jaar na afhandeling."],
            ["Leads en offerteaanvragen", "maximaal 2 jaar na de laatste activiteit, tenzij een geschil of wettelijke verplichting een langere termijn rechtvaardigt."],
            ["Klantdossiers en accountgegevens", "gedurende het account en maximaal 2 jaar na beëindiging, tenzij eerder verwijdering wordt gevraagd of bewaring nodig is."],
            ["Marketingtoestemmingen", "tot intrekking van toestemming of uitschrijving; bewijs van toestemming kan langer worden bewaard voor verantwoordingsdoeleinden."],
            ["Administratieve gegevens", "doorgaans 7 jaar wanneer fiscale wetgeving dit vereist."],
            ["Technische logs", "30 tot 180 dagen, tenzij onderzoek naar misbruik of incidenten langer bewaren noodzakelijk maakt."],
          ]?.map(([label, desc], i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              <span><strong>{label}:</strong> {desc}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: 8,
    title: "8. Dienstverleners en internationale doorgifte",
    content: (
      <div className="space-y-3">
        <p>
          Voor hosting, formulieren, e-mail, CRM, analyse of automatisering kunnen wij verwerkers inschakelen.
          Met verwerkers sluiten wij waar vereist een verwerkersovereenkomst.
        </p>
        <p>
          Indien persoonsgegevens buiten de Europese Economische Ruimte worden verwerkt, zorgen wij voor een
          geldige doorgiftegrondslag en passende waarborgen, bijvoorbeeld een adequaatheidsbesluit of
          goedgekeurde standaardcontractbepalingen.
        </p>
      </div>
    ),
  },
  {
    id: 9,
    title: "9. Beveiliging",
    content: (
      <p>
        Wij treffen passende technische en organisatorische maatregelen tegen verlies, ongeoorloofde toegang en
        misbruik. Denk aan toegangsbeperking, beveiligde verbindingen, leveranciersbeoordeling en periodieke
        controle van bewaartermijnen. Geen enkel digitaal systeem is volledig risicoloos. Meld een vermoedelijk
        datalek of beveiligingsprobleem via{' '}
        <a href="mailto:security@verduurzaamwijzer.nu" className="text-primary hover:underline">
          security@verduurzaamwijzer.nu
        </a>.
      </p>
    ),
  },
  {
    id: 10,
    title: "10. Uw privacyrechten",
    content: (
      <div className="space-y-3">
        <p>
          U kunt verzoeken om inzage, rectificatie, verwijdering, beperking van verwerking, overdraagbaarheid en
          bezwaar, voor zover de AVG daarin voorziet. Toestemming kunt u op ieder moment intrekken, zonder dat
          dit eerdere rechtmatige verwerking aantast.
        </p>
        <p>
          Stuur uw verzoek naar{' '}
          <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline">
            contact@verduurzaamwijzer.nu
          </a>. Wij reageren in beginsel binnen één maand, met de wettelijke mogelijkheid tot verlenging.
        </p>
      </div>
    ),
  },
  {
    id: 11,
    title: "11. Klachten en wijzigingen",
    content: (
      <div className="space-y-3">
        <p>
          Heeft u een klacht over de verwerking van uw persoonsgegevens? Neem dan eerst contact met ons op, zodat
          wij naar een oplossing kunnen zoeken. U heeft daarnaast het recht een klacht in te dienen bij de{' '}
          <a
            href="https://www.autoriteitpersoonsgegevens.nl"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Autoriteit Persoonsgegevens
          </a>.
        </p>
        <p>
          Wij kunnen deze verklaring aanpassen als onze dienstverlening of regelgeving verandert. De actuele
          versie wordt op de website gepubliceerd. Bij wezenlijke wijzigingen informeren wij u op passende wijze.
        </p>
      </div>
    ),
  },
];

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Hero */}
      <section className="bg-secondary text-secondary-foreground pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-secondary-foreground/60 mb-4">
              <Link to="/homepage" className="hover:text-secondary-foreground/80 transition-colors">Home</Link>
              <Icon name="ChevronRight" size={14} />
              <span>Privacyverklaring</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-secondary-foreground mb-3">
              Privacyverklaring
            </h1>
            <p className="text-secondary-foreground/70 text-base mb-6">
              Hoe Verduurzaamwijzer.nu omgaat met persoonsgegevens, cookies en gegevensdeling
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-secondary-foreground/60">
              <span className="flex items-center gap-1.5">
                <Icon name="FileText" size={14} />
                Versie 2.0
              </span>
              <span className="flex items-center gap-1.5">
                <Icon name="Calendar" size={14} />
                9 oktober 2026
              </span>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Table of Contents — sticky sidebar */}
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">Inhoud</h2>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#section-${s?.id}`}
                      className="block text-sm text-foreground/60 hover:text-primary transition-colors py-0.5 leading-snug"
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              <div className="space-y-10">
                {sections?.map((section, index) => (
                  <motion.div
                    key={section?.id}
                    id={`section-${section?.id}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.4, delay: index * 0.03 }}
                    className="scroll-mt-24"
                  >
                    <h2 className="font-heading text-lg font-semibold text-foreground mb-3 pb-2 border-b border-border/50">
                      {section?.title}
                    </h2>
                    <div className="text-foreground/75 leading-relaxed text-sm sm:text-base">
                      {section?.content}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Contact card */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="mt-12 bg-primary/5 border border-primary/20 rounded-xl p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Mail" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">Vragen over privacy?</h3>
                    <p className="text-sm text-foreground/70 mb-3">
                      Neem contact op met De Content Factory BV via{' '}
                      <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline font-medium">
                        contact@verduurzaamwijzer.nu
                      </a>{' '}
                      of bel{' '}
                      <a href="tel:0647650537" className="text-primary hover:underline font-medium">
                        06 4765 0537
                      </a>.
                    </p>
                    <p className="text-xs text-foreground/50">
                      De Content Factory BV · Oude Middenweg 17, 2491 AC Den Haag · KvK 84512490
                    </p>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
